import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { DashboardMonthData } from '../../hooks/useCaapApi';

interface HeroCardProps {
  months: DashboardMonthData[];
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

export const HeroCard: React.FC<HeroCardProps> = ({ months }) => {
  const current = months[months.length - 1];
  const previous = months.length > 1 ? months[months.length - 2] : null;

  const currentAccounts = current?.assignedCount ?? 0;
  const previousAccounts = previous?.assignedCount ?? 0;
  const accountDelta = currentAccounts - previousAccounts;

  const currentPayoff = current?.payoffBalance ?? 0;
  const avgBalance = currentAccounts > 0 ? currentPayoff / currentAccounts : 0;

  const chartData = months.map((m) => ({
    label: m.monthName.slice(0, 3),
    accounts: m.assignedCount,
    fullLabel: `${m.monthName.slice(0, 3)} '${String(m.year).slice(2)}`,
  }));

  return (
    <div className="bg-surface-alt rounded-lg shadow-sm border border-border-default border-l-4 border-l-blue-600 p-6 mb-8">
      <div className="flex items-start justify-between mb-1">
        <div>
          <span className="inline-block bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs font-semibold px-2 py-0.5 rounded mb-2">
            STEP 7
          </span>
          <h2 className="text-lg font-semibold text-primary">Assign to Collections</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-4">
        {/* Left: Key Metrics */}
        <div className="lg:col-span-1 space-y-4">
          <div>
            <p className="text-sm font-medium text-muted">Accounts Assigned</p>
            <div className="flex items-baseline gap-3">
              <span className="text-4xl font-bold text-primary">
                {currentAccounts > 0 ? currentAccounts : '\u2014'}
              </span>
              {previous && accountDelta !== 0 && (
                <span
                  className={`inline-flex items-center text-sm font-medium ${
                    accountDelta > 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {accountDelta > 0 ? (
                    <svg className="w-4 h-4 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  )}
                  {accountDelta > 0 ? '+' : ''}{accountDelta} from last month
                </span>
              )}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-muted">Total Payoff Balance</p>
            <span className="text-2xl font-bold text-primary">
              {currentPayoff > 0 ? formatCurrency(currentPayoff) : '\u2014'}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-muted">Avg Balance per Account</p>
            <span className="text-lg font-semibold text-secondary">
              {avgBalance > 0 ? formatCurrency(avgBalance) : '\u2014'}
            </span>
          </div>
        </div>

        {/* Right: Bar Chart */}
        <div className="lg:col-span-2">
          <p className="text-xs font-medium text-muted uppercase tracking-wide mb-2">
            Accounts Assigned — Trailing 12 Months
          </p>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={192}>
              <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip
                  formatter={(value) => [String(value), 'Accounts']}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ''}
                  contentStyle={{ fontSize: 13, borderRadius: 6 }}
                />
                <Bar dataKey="accounts" radius={[3, 3, 0, 0]}>
                  {chartData.map((_, index) => (
                    <Cell
                      key={index}
                      fill={index === chartData.length - 1 ? '#2563eb' : '#3b82f6'}
                      stroke={index === chartData.length - 1 ? '#93c5fd' : 'none'}
                      strokeWidth={index === chartData.length - 1 ? 2 : 0}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-muted text-sm">
              No data yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
